// Two layouts:
// - "feed": web feed–style — chips → 4:3 hero → bottom fade + optional sender row.
// - "native": mirrors noticeboard-native InfoNoticeCardWithBackground / NewEventNoticeCard —
//   tall min-height hero, centred copy, uniform rgba(0,0,0,0.4) overlay on images, vertical
//   primary gradient when no image; event meta as centred "Date: …" lines; no in-card chips/footer.
import React from "react";
import { Box, Typography, Paper, Chip, Stack, useTheme } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import { alpha } from "@mui/material/styles";
import { lightTheme } from "../../styles/theme";
import type { NoticeFeedCardType } from "../../utils/noticeFeedChips";
import {
  getAudienceCircleChipBackground,
  getNoticeFeedTypeChipBackground,
  getNoticeFeedTypeChipLabel,
  getNoticeFeedUrgentChipBackground,
} from "../../utils/noticeFeedChips";
import { formatEventDisplayDate } from "../../utils/eventDateDisplay";
import { htmlToPlainText } from "../../utils/noticeBodyHtml";

export type NoticeCardPreviewLayout = "feed" | "native";

interface NoticeCardPreviewProps {
  title: string;
  body: string;
  eventStartDate?: Date | null;
  eventStartTime?: string;
  eventLocation?: string;
  eventBackgroundImage?: string;
  isEvent?: boolean;
  /** "feed" for template modals / web card; "native" for wizard review parity with the app. */
  layout?: NoticeCardPreviewLayout;
  feedChipStrip?: {
    cardType: NoticeFeedCardType;
    isUrgent: boolean;
    circleLabel: string;
  };
  /** Shown below hero in "feed" layout when provided. Ignored for "native". */
  sender?: string;
  postedAt?: Date;
}

const compactChipSx = {
  fontWeight: 600,
  fontSize: 10,
  height: 22,
  "& .MuiChip-label": { px: 1 },
} as const;

const truncateBody = (text: string, max: number): string => {
  const plain = htmlToPlainText(text || "").trim();
  if (!plain) return "";
  if (plain.length <= max) return plain;
  return `${plain.slice(0, max).trim()}…`;
};

const nativeTitleShadow = {
  textShadow: "0 2px 4px rgba(0, 0, 0, 0.75)",
} as const;

const nativeBodyShadow = {
  textShadow: "0 1px 3px rgba(0, 0, 0, 0.75)",
} as const;

const NoticeCardPreview: React.FC<NoticeCardPreviewProps> = ({
  title,
  body,
  eventStartDate,
  eventStartTime,
  eventLocation,
  eventBackgroundImage,
  isEvent = true,
  layout = "feed",
  feedChipStrip,
  sender,
  postedAt,
}) => {
  const theme = useTheme();
  const formattedDate =
    formatEventDisplayDate(eventStartDate || null) || "TBC";
  const hasImageBackground =
    Boolean(eventBackgroundImage && eventBackgroundImage.trim() !== "");
  const displayBodyFeed = truncateBody(body || "", 140);
  const displayBodyNative = truncateBody(body || "", 400);
  const showMetaFooter =
    layout === "feed" && (Boolean(sender?.trim()) || postedAt instanceof Date);

  const footerSender = sender?.trim() || "Preview";
  const footerDate = postedAt instanceof Date ? postedAt : null;

  const metaRowSx = {
    display: "flex",
    alignItems: "flex-start",
    gap: 0.75,
    color: alpha(theme.palette.common.white, 0.95),
  } as const;

  const metaIconSx = { fontSize: 16, opacity: 0.9, mt: "1px", flexShrink: 0 };

  /** Native-aligned vertical gradient (primary → darker primary → accent), same stops as RN LinearGradient. */
  const nativeGradientBg = `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 52%, ${theme.palette.secondary.main} 100%)`;

  const overlayContentFeed = (
    <>
      <Typography
        sx={{
          color: theme.palette.common.white,
          fontWeight: 700,
          fontSize: "1.05rem",
          lineHeight: 1.25,
          mb: displayBodyFeed ? 0.85 : isEvent ? 1 : 0,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {title || "Untitled"}
      </Typography>
      {displayBodyFeed ? (
        <Typography
          sx={{
            color: alpha(theme.palette.common.white, 0.88),
            fontSize: "0.8125rem",
            lineHeight: 1.45,
            fontWeight: 400,
            mb: isEvent ? 1.1 : 0,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {displayBodyFeed}
        </Typography>
      ) : null}
      {isEvent && (
        <Stack spacing={0.65} sx={{ mt: 0.25 }}>
          <Box sx={metaRowSx}>
            <CalendarTodayIcon sx={metaIconSx} />
            <Typography variant="body2" sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
              {formattedDate}
            </Typography>
          </Box>
          <Box sx={metaRowSx}>
            <ScheduleIcon sx={metaIconSx} />
            <Typography variant="body2" sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
              {eventStartTime?.trim() || "TBC"}
            </Typography>
          </Box>
          <Box sx={metaRowSx}>
            <LocationOnIcon sx={metaIconSx} />
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 600,
                lineHeight: 1.35,
                wordBreak: "break-word",
              }}
            >
              {eventLocation?.trim() || "TBC"}
            </Typography>
          </Box>
        </Stack>
      )}
    </>
  );

  const overlayContentNative = (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 3,
        py: 3,
        boxSizing: "border-box",
      }}
    >
      {!hasImageBackground && isEvent ? (
        <Box
          component="img"
          src="/icons/events.webp"
          alt=""
          sx={{ width: 150, height: 150, objectFit: "contain", mb: 2 }}
        />
      ) : null}
      <Typography
        sx={{
          color: theme.palette.common.white,
          fontWeight: 700,
          fontSize: "1.5rem",
          lineHeight: 1.3,
          mb: displayBodyNative ? 1.5 : isEvent ? 1 : 0,
          ...nativeTitleShadow,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          width: "100%",
        }}
      >
        {title || "Untitled"}
      </Typography>
      {displayBodyNative ? (
        <Typography
          sx={{
            color: "#E0E0E0",
            fontSize: "1rem",
            lineHeight: 1.5,
            fontWeight: 400,
            mb: isEvent ? 2 : 0,
            ...nativeBodyShadow,
            display: "-webkit-box",
            WebkitLineClamp: 4,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            width: "100%",
          }}
        >
          {displayBodyNative}
        </Typography>
      ) : null}
      {isEvent ? (
        <Stack
          spacing={1.25}
          sx={{
            mt: displayBodyNative ? 0 : 1,
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.common.white,
              fontSize: "1rem",
              fontWeight: 700,
              ...nativeBodyShadow,
              width: "100%",
            }}
          >
            Date: {formattedDate || "TBC"}
          </Typography>
          <Typography
            sx={{
              color: theme.palette.common.white,
              fontSize: "1rem",
              fontWeight: 700,
              ...nativeBodyShadow,
              width: "100%",
            }}
          >
            Time: {eventStartTime?.trim() || "TBC"}
          </Typography>
          <Typography
            sx={{
              color: theme.palette.common.white,
              fontSize: "1rem",
              fontWeight: 700,
              ...nativeBodyShadow,
              width: "100%",
              wordBreak: "break-word",
            }}
          >
            {eventLocation?.trim() || "TBC"}
          </Typography>
        </Stack>
      ) : null}
    </Box>
  );

  const showChipStrip = layout === "feed" && Boolean(feedChipStrip);

  const heroFeed = (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "4 / 3",
        maxHeight: 268,
        bgcolor: theme.palette.grey[900],
        overflow: "hidden",
      }}
    >
      {hasImageBackground ? (
        <Box
          component="img"
          src={eventBackgroundImage?.trim()}
          alt={title ? `Event: ${title}` : "Event image"}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(160deg, ${theme.palette.gradients.event[0]} 0%, ${theme.palette.gradients.event[1]} 45%, ${theme.palette.gradients.event[2]} 100%)`,
          }}
        />
      )}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(
                to top,
                ${alpha(lightTheme.colors.pureBlack, 0.82)} 0%,
                ${alpha(lightTheme.colors.pureBlack, 0.35)} 42%,
                transparent 72%
              )`,
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          p: 1.5,
          pb: 1.65,
          boxSizing: "border-box",
        }}
      >
        {overlayContentFeed}
      </Box>
    </Box>
  );

  const nativeHeroMin = isEvent
    ? "clamp(320px, 36vh, 560px)"
    : "clamp(280px, 35vh, 520px)";

  const heroNative = (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: nativeHeroMin,
        bgcolor: theme.palette.grey[900],
        overflow: "hidden",
        borderRadius: 2,
      }}
    >
      {hasImageBackground ? (
        <>
          <Box
            component="img"
            src={eventBackgroundImage?.trim()}
            alt=""
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: 2,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: alpha(theme.palette.common.black, 0.4),
              borderRadius: 2,
              pointerEvents: "none",
            }}
          />
        </>
      ) : (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: 2,
            background: nativeGradientBg,
          }}
        />
      )}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          minHeight: nativeHeroMin,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 2,
        }}
      >
        {overlayContentNative}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ mb: 3 }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 400,
          display: "flex",
          flexDirection: "column",
          borderRadius: layout === "native" ? 2 : 3,
          overflow: "hidden",
          position: "relative",
          border: layout === "native" ? "none" : "1px solid",
          borderColor: theme.palette.event.border,
          bgcolor: layout === "native" ? "transparent" : theme.palette.background.paper,
          boxShadow: "none",
          mx: "auto",
        }}
      >
        {showChipStrip && feedChipStrip ? (
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.75,
              px: 1.25,
              py: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Chip
              size="small"
              label={getNoticeFeedTypeChipLabel(feedChipStrip.cardType)}
              sx={{
                ...compactChipSx,
                bgcolor: getNoticeFeedTypeChipBackground(
                  theme,
                  feedChipStrip.cardType
                ),
                color: lightTheme.colors.white,
              }}
            />
            {feedChipStrip.isUrgent && (
              <Chip
                size="small"
                label="Urgent"
                sx={{
                  ...compactChipSx,
                  bgcolor: getNoticeFeedUrgentChipBackground(theme),
                  color: lightTheme.colors.white,
                }}
              />
            )}
            <Chip
              size="small"
              label={feedChipStrip.circleLabel}
              sx={{
                ...compactChipSx,
                bgcolor: getAudienceCircleChipBackground(
                  theme,
                  feedChipStrip.circleLabel
                ),
                color: lightTheme.colors.white,
              }}
            />
          </Box>
        ) : null}

        {layout === "native" ? heroNative : heroFeed}

        {showMetaFooter ? (
          <Box
            sx={{
              px: 1.5,
              py: 1.15,
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              alignItems="center"
              useFlexGap
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <PersonIcon
                  sx={{ fontSize: 15, opacity: 0.55, color: "text.secondary" }}
                />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {footerSender}
                </Typography>
              </Stack>
              {footerDate ? (
                <>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: "divider", px: 0.25 }}
                  >
                    •
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <CalendarTodayIcon
                      sx={{
                        fontSize: 15,
                        opacity: 0.55,
                        color: "text.secondary",
                      }}
                    />
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {footerDate.toLocaleString()}
                    </Typography>
                  </Stack>
                </>
              ) : null}
            </Stack>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
};

export default NoticeCardPreview;
